import { Form, FormInstance } from 'antd';
import { Colors, IcChevronDown, Text } from 'basic-ui-lib';
import BaseInput from 'basic-ui-lib/components/Input/BaseInput';
import { BusinessType } from 'modules/store/services/constants';
import useGetDetailStore from 'modules/store/services/hooks/useGetDetailStore';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import BusinessTypePicker from './BusinessTypePicker';
import { useEffect, useState } from 'react';
import TimezonePicker from './TimezonePicker';
import { setPageLoading } from 'widgets/PageLoading';
import storeApis from 'modules/store/services/apis';
import toast from 'utils/toast';
import navigate from 'utils/navigate';
import { getMessageError } from 'services/response';
import { FormStoreValues } from '..';

interface FormStoreProps {
  form: FormInstance<FormStoreValues>;
}

const FormStore = ({ form }: FormStoreProps) => {
  const { id } = useParams<{ id: string }>();
  const { detailStore } = useGetDetailStore(id || '');
  const [openBusinessType, setOpenBusinessType] = useState(false);
  const [openTimezone, setOpenTimezone] = useState(false);

  useEffect(() => {
    if (detailStore) {
      form.setFieldsValue({
        firstName: detailStore.firstName,
        lastName: detailStore.lastName,
        businessName: detailStore.businessName,
        phone: detailStore.phone,
        email: detailStore.email,
        address: detailStore.address,
        city: detailStore.city,
        state: detailStore.state,
        zip: detailStore.zip,
        timezone: detailStore.timezone,
        businessType: detailStore.businessType,
      });
    }
  }, [detailStore]);

  const handleSubmit = async(values: FormStoreValues) => {
    try {
      setPageLoading(true);
      let res = null;
      if (id) {
        res = await storeApis.updateStore({ storeId: id,...values });
      } else {
        res = await storeApis.createStore(values);
      }

      if (res?.data?.data) {
        toast.success(id ? 'Update store successfully' : 'Create store successfully');
        navigate('/store');
      }
    } catch (error) {
      toast.error(getMessageError(error) || (id ? 'Update store failed' : 'Create store failed'));
    } finally {
      setPageLoading(false);
    }
  };

  return (
    <Form
      layout='vertical'
      form={form}
      className='form-styled'
      onFinish={handleSubmit}
      >
      <Form.Item label='First Name' name='firstName' rules={[{ required: true, message: 'Please enter first name' }]}>
        <BaseInput placeholder='Please enter ...' />
      </Form.Item>
      <Form.Item label='Last Name' name='lastName' rules={[{ required: true, message: 'Please enter last name' }]}>
        <BaseInput placeholder='Please enter ...' />
      </Form.Item>
      <Form.Item
        label='Business Name'
        name='businessName'
        rules={[{ required: true, message: 'Please enter business name' }]}
      >
        <BaseInput placeholder='Please enter ...' />
      </Form.Item>
      <Form.Item
        label='Phone'
        name='phone'
        rules={[
          { required: true, message: 'Please enter phone number' },
          { pattern: /^\d+$/, message: 'Please enter a valid phone number' },
        ]}
      >
        <BaseInput placeholder='Please enter ...' />
      </Form.Item>
      <Form.Item label='Email' name='email' rules={[{ type: 'email', message: 'Please enter a valid email' }]}>
        <BaseInput placeholder='Please enter ...' />
      </Form.Item>
      <Form.Item label='Street' name='address'>
        <BaseInput placeholder='Please enter ...' />
      </Form.Item>
      <Form.Item label='City' name='city'>
        <BaseInput placeholder='Please enter ...' />
      </Form.Item>
      <Form.Item label='State' name='state'>
        <BaseInput placeholder='Please enter ...' />
      </Form.Item>
      <Form.Item label='Zip' name='zip'>
        <BaseInput placeholder='Please enter ...' />
      </Form.Item>
      <Form.Item label='Timezone' name='timezone'>
        <Form.Item shouldUpdate noStyle>
          {() => (
            <BusinessTypeContent onClick={() => setOpenTimezone(true)}>
              <Text body_1 medium>
                {form.getFieldValue('timezone') || 'Please select ...'}
              </Text>
              <IcChevronDown width={16} height={16} />
            </BusinessTypeContent>
          )}
        </Form.Item>
      </Form.Item>
      <Form.Item label='Business Type' name='businessType'>
        <Form.Item shouldUpdate noStyle>
          {() => (
             <BusinessTypeContent onClick={() => setOpenBusinessType(true)}>
              <Text body_1 medium>
                {form.getFieldValue('businessType') || 'Please select ...'}
              </Text>
              <IcChevronDown width={16} height={16} />
            </BusinessTypeContent>
          )}
        </Form.Item>
      </Form.Item>
      <Form.Item shouldUpdate hidden>
        {() => (
          <TimezonePicker
            open={openTimezone}
            onClose={() => setOpenTimezone(false)}
            value={form.getFieldValue('timezone')}
            onChange={(value) => form.setFieldsValue({ timezone: value })}
          />
        )}
      </Form.Item>
      <Form.Item shouldUpdate hidden>
        {() => (
          <BusinessTypePicker
            open={openBusinessType}
            onClose={() => setOpenBusinessType(false)}
            value={form.getFieldValue('businessType')}
            onChange={(value) => form.setFieldsValue({ businessType: value as BusinessType })}
          />
        )}
      </Form.Item>
    </Form>
  );
};

export default FormStore;

const BusinessTypeContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0 11px;
  height: 48px;
  border: 1px solid ${Colors.neutral.border[3]};
  border-radius: 4px;
`;
